import { EditOutlined, MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  App,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Upload,
} from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';
import { PropertyArea } from '../../../types/property';

const { TextArea } = Input;

// Helper to manage file uploads
const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e && e.fileList;
};

const PropertyAreaList: React.FC = () => {
  const [propertyAreaList, setPropertyAreaList] = useState<PropertyArea[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingPropertyArea, setEditingPropertyArea] = useState<PropertyArea | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();

  const fetchPropertyAreas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('property-areas');
      if (response.data && response.data.status === 'success' && Array.isArray(response.data.data)) {
        const transformedData = response.data.data.map((item: any) => ({
          id: item.id,
          name: item.name,
          is_active: item.is_active,
        }));
        setPropertyAreaList(transformedData);
      } else {
        console.error("Gagal memuat data dengan format yang benar:", response.data);
        setPropertyAreaList([]);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Gagal mengambil data property area';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchPropertyAreas();
  }, [fetchPropertyAreas]);

  const showModal = (propertyArea: PropertyArea | null = null) => {
    setEditingPropertyArea(propertyArea);
    if (propertyArea) {
      // In a real app, you'd fetch the full details for the form
      form.setFieldsValue({
        name: propertyArea.name,
        // Set other fields if available, otherwise they'll be blank for editing
      });
    } else {
      form.resetFields();
    }
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingPropertyArea(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('address', values.address);
      formData.append('phone_number', values.phone_number);
      formData.append('description', values.description);

      // Convert arrays/objects to JSON strings
      formData.append('promotion_labels', JSON.stringify(values.promotion_labels || []));
      formData.append('project_info', JSON.stringify(values.project_info || []));

      // Append files
      if (values.header_logo && values.header_logo[0]) {
        formData.append('header_logo', values.header_logo[0].originFileObj);
      }
      if (values.e_brochure && values.e_brochure[0]) {
        formData.append('e_brochure', values.e_brochure[0].originFileObj);
      }
      if (values.price_list && values.price_list[0]) {
        formData.append('price_list', values.price_list[0].originFileObj);
      }

      // Handle photos with captions
      if (values.photos && values.photos.length > 0) {
        const photoCaptions = values.photos.map((p: any) => p.caption || '');
        formData.append('photo_captions', JSON.stringify(photoCaptions));
        values.photos.forEach((photo: any) => {
          if (photo.originFileObj) {
            formData.append('photos', photo.originFileObj);
          }
        });
      }

      const apiConfig = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };

      if (editingPropertyArea) {
        await api.put(`property-areas/${editingPropertyArea.id}`, formData, apiConfig);
        message.success('Property Area berhasil diperbarui');
      } else {
        await api.post('property-areas', formData, apiConfig);
        message.success('Property Area berhasil ditambahkan');
      }

      handleCancel();
      fetchPropertyAreas();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Operasi gagal. Silakan coba lagi.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record: PropertyArea) => {
    try {
      setLoading(true);
      const action = record.is_active ? 'deactivated' : 'reactivated';
      const endpoint = record.is_active
        ? `/property-areas/${record.id}`
        : `/property-areas/${record.id}/reactivate`;

      if (record.is_active) {
        await api.delete(endpoint);
      } else {
        await api.put(endpoint);
      }

      message.success(`Property area ${action} berhasil`);
      fetchPropertyAreas();
    } catch (error) {
      const action = record.is_active ? 'deactivate' : 'reactivate';
      message.error(`Gagal ${action} property area`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns: any[] = [
    {
      title: 'Nama Property Area',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: PropertyArea, b: PropertyArea) => a.name.localeCompare(b.name),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (text: boolean) => (
        <Tag color={text ? 'green' : 'red'}>
          {text ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Aksi',
      key: 'actions',
      width: 150,
      render: (_: any, record: PropertyArea) => (
        <Space size="middle">
          <Button
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            disabled={!record.is_active}
          />
          <Popconfirm
            title={`Apakah Anda yakin ingin ${record.is_active ? 'deactivate' : 'reactivate'
              } tipe ini?`}
            onConfirm={() => handleToggleActive(record)}
            okText="Ya"
            cancelText="Tidak"
          >
            <Switch
              checked={record.is_active}
              checkedChildren="Active"
              unCheckedChildren="Inactive"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <React.Fragment>
      <Card
        title="Property Area"
        bordered={false}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Tambah
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={propertyAreaList}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
      <Modal
        title={editingPropertyArea ? 'Edit Property Area' : 'Tambah Property Area'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={loading}
        width={800}
      >
        <Form form={form} layout="vertical" name="propertyAreaForm" initialValues={{ project_info: [''] }}>
          <Form.Item name="name" label="Nama Property Area" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="address" label="Alamat" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item name="phone_number" label="Nomor Telepon" rules={[{ required: true, pattern: /^\d+$/, message: 'Hanya angka yang diperbolehkan' }]}>
            <Input type="number" />
          </Form.Item>

          <Form.Item name="description" label="Deskripsi" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item name="promotion_labels" label="Label Promosi">
            <Select mode="tags" style={{ width: '100%' }} placeholder="Contoh: Diskon DP 10%" />
          </Form.Item>

          <Form.Item label="Info Proyek">
            <Form.List name="project_info">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                      <Form.Item {...restField} name={[name, 'key']} rules={[{ required: true, message: 'Key harus diisi' }]}>
                        <Input placeholder="Key (Contoh: Tipe Unit)" />
                      </Form.Item>
                      <Form.Item {...restField} name={[name, 'value']} rules={[{ required: true, message: 'Value harus diisi' }]}>
                        <Input placeholder="Value (Contoh: Cluster)" />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(name)} />
                    </Space>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Tambah Info Proyek
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="header_logo" label="Header Logo" valuePropName="fileList" getValueFromEvent={normFile} extra="Format file JPG, JPEG, PNG. Max: 3MB.">
                <Upload
                  listType="picture"
                  maxCount={1}
                  accept=".jpg,.jpeg,.png"
                  beforeUpload={(file) => {
                    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
                    if (!isJpgOrPng) {
                      message.error('Format file yang diizinkan JPG, JPEG, PNG');
                    }
                    const isLt3M = file.size / 1024 / 1024 < 3;
                    if (!isLt3M) {
                      message.error('File harus kurang dari 3MB');
                    }
                    return isJpgOrPng && isLt3M ? false : Upload.LIST_IGNORE;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Pilih Gambar</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="e_brochure" label="E-Brochure" valuePropName="fileList" getValueFromEvent={normFile} extra="Hanya file PDF. Max: 3MB.">
                <Upload
                  maxCount={1}
                  accept=".pdf"
                  beforeUpload={(file) => {
                    const isPdf = file.type === 'application/pdf';
                    if (!isPdf) {
                      message.error('Format file yang diizinkan PDF');
                    }
                    const isLt3M = file.size / 1024 / 1024 < 3;
                    if (!isLt3M) {
                      message.error('File harus kurang dari 3MB');
                    }
                    return isPdf && isLt3M ? false : Upload.LIST_IGNORE;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Pilih PDF</Button>
                </Upload>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="price_list" label="Price List" valuePropName="fileList" getValueFromEvent={normFile} extra="Hanya file PDF. Max: 3MB.">
                <Upload
                  maxCount={1}
                  accept=".pdf"
                  beforeUpload={(file) => {
                    const isPdf = file.type === 'application/pdf';
                    if (!isPdf) {
                      message.error('Format file yang diizinkan PDF');
                    }
                    const isLt3M = file.size / 1024 / 1024 < 3;
                    if (!isLt3M) {
                      message.error('File harus kurang dari 3MB');
                    }
                    return isPdf && isLt3M ? false : Upload.LIST_IGNORE;
                  }}
                >
                  <Button icon={<UploadOutlined />}>Pilih PDF</Button>
                </Upload>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Foto-foto Property Area">
            <Form.List name="photos">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row key={key} gutter={16} align="top">
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'originFileObj']}
                          valuePropName='fileList' // Correct prop for Upload
                          getValueFromEvent={normFile}
                          rules={[{ required: true, message: 'File harus di-upload' }]}
                          extra="Hanya file JPG/PNG. Maksimal 3MB."
                        >
                          <Upload
                            listType="picture"
                            maxCount={1}
                            accept=".jpg,.jpeg,.png"
                            beforeUpload={(file) => {
                              const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
                              if (!isJpgOrPng) {
                                message.error('Anda hanya bisa mengupload file JPG/PNG!');
                              }
                              const isLt3M = file.size / 1024 / 1024 < 3;
                              if (!isLt3M) {
                                message.error('File harus lebih kecil dari 3MB!');
                              }
                              return isJpgOrPng && isLt3M ? false : Upload.LIST_IGNORE;
                            }}
                          >
                            <Button icon={<UploadOutlined />}>Pilih Gambar</Button>
                          </Upload>
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item {...restField} name={[name, 'caption']}>
                          <Input placeholder="Caption untuk foto ini" />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ marginTop: '8px' }} />
                      </Col>
                    </Row>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Tambah Foto
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default PropertyAreaList;